/**
 * CPT Core BWE colour-coded HTML for Section 9 & 11 template fields (display only).
 * Protocol/export paths should use cptHtmlToPlainText().
 */

import { ADVERSE_EVENTS_TEMPLATE_FIELD_KEYS } from '../constants/templateLanguageFieldRows';
import { tryParseMarkdownPipeTable } from './normalizeTemplateLanguagePlainText';

export const CPT_SECTION_9_FIELD_KEYS = new Set(ADVERSE_EVENTS_TEMPLATE_FIELD_KEYS);

/** Section 11 regulatory_requirements template checkbox fields (not comments). */
export const CPT_SECTION_11_FIELD_KEYS = new Set([
  'regulatory_ethical_considerations',
  'investigator_responsibilities',
  'sponsor_responsibilities',
  'informed_consent_rescreening',
  'informed_consent_remaining_samples',
  'committees',
  'insurance_indemnity',
  'risk_based_quality_management',
  'data_governance',
  'data_protection',
  'source_data',
  'protocol_deviations',
  'early_site_closure',
  'data_dissemination',
]);

export const CPT_HTML_CLASS = {
  common: 'cpt-common',
  suggested: 'cpt-suggested',
  variable: 'cpt-variable',
  example: 'cpt-example',
};

export const CPT_COLOR_LEGEND = [
  { label: 'Common text', className: CPT_HTML_CLASS.common, color: '#212121', fontStyle: 'normal' },
  { label: 'Suggested text', className: CPT_HTML_CLASS.suggested, color: '#212121', fontStyle: 'normal' },
  { label: 'Variable text', className: CPT_HTML_CLASS.variable, color: '#1565C0', fontStyle: 'normal' },
  { label: 'Example text (only if intake-supported)', className: CPT_HTML_CLASS.example, color: '#2E7D32', fontStyle: 'italic' },
];

const ALLOWED_CLASS = new Set(Object.values(CPT_HTML_CLASS));

export const shouldUseCptColoredOutput = (fieldName) =>
  CPT_SECTION_9_FIELD_KEYS.has(fieldName) || CPT_SECTION_11_FIELD_KEYS.has(fieldName);

export const isCptStyledHtml = (value) => {
  if (value == null) return false;
  const s = String(value);
  return (
    /class\s*=\s*["']cpt-(common|suggested|variable|example)["']/i.test(s) ||
    /<span[^>]*\bstyle\s*=\s*["'][^"']*\bcolor\s*:/i.test(s) ||
    /<font[^>]*\bcolor\s*=/i.test(s)
  );
};

/** Map inline colour / font tags from model output to CPT span classes for display. */
export const normalizeCptMarkupForDisplay = (value) => {
  if (value == null) return '';
  let s = String(value).trim();
  if (!s) return '';

  if (/class\s*=\s*["']cpt-(common|suggested|variable|example)["']/i.test(s)) {
    return s;
  }

  s = s.replace(
    /<span([^>]*)\bstyle\s*=\s*["']([^"']*)["']([^>]*)>/gi,
    (match, before, style, after) => {
      const lower = style.toLowerCase();
      let cls = CPT_HTML_CLASS.common;
      if (/color\s*:\s*#?1565c0|color\s*:\s*blue/i.test(lower)) cls = CPT_HTML_CLASS.variable;
      else if (/color\s*:\s*#?2e7d32|color\s*:\s*green/i.test(lower)) cls = CPT_HTML_CLASS.example;
      else if (/font-style\s*:\s*italic/i.test(lower) && /green/i.test(lower)) cls = CPT_HTML_CLASS.example;
      return `<span class="${cls}">`;
    }
  );

  s = s.replace(/<font[^>]*\bcolor\s*=\s*["']?#?1565c0["']?[^>]*>/gi, `<span class="${CPT_HTML_CLASS.variable}">`);
  s = s.replace(/<font[^>]*\bcolor\s*=\s*["']?#?2e7d32["']?[^>]*>/gi, `<span class="${CPT_HTML_CLASS.example}">`);
  s = s.replace(/<font[^>]*\bcolor\s*=\s*["']?blue["']?[^>]*>/gi, `<span class="${CPT_HTML_CLASS.variable}">`);
  s = s.replace(/<font[^>]*\bcolor\s*=\s*["']?green["']?[^>]*>/gi, `<span class="${CPT_HTML_CLASS.example}">`);
  s = s.replace(/<\/font>/gi, '</span>');

  return s;
};

export const cptHtmlToPlainText = (value) => {
  if (value == null) return '';
  let s = String(value);
  if (!isCptStyledHtml(s)) return s;
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/span>/gi, '');
  s = s.replace(/<span[^>]*>/gi, '');
  s = s.replace(/<[^>]+>/g, '');
  s = s.replace(/&nbsp;/gi, ' ');
  s = s.replace(/&amp;/g, '&');
  s = s.replace(/&lt;/g, '<');
  s = s.replace(/&gt;/g, '>');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
};

/** Bare table-structure tags allowed through (no attributes) so tabular CPT
 * content (e.g. AE/SAE reporting schedules) can render as a real grid instead
 * of collapsing to "Label: value" lines. */
const TABLE_TAG_NAMES = ['table', 'thead', 'tbody', 'tr', 'th', 'td'];
const TABLE_TAG_ALTERNATION = TABLE_TAG_NAMES.join('|');

/** Whitelist CPT span classes, <br>, and bare table-structure tags. */
export const sanitizeCptTemplateHtml = (value) => {
  if (value == null) return '';
  let s = normalizeCptMarkupForDisplay(String(value).trim());
  if (!s) return '';

  if (!isCptStyledHtml(s)) {
    return '';
  }

  s = s.replace(/<script[\s\S]*?<\/script>/gi, '');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '');
  s = s.replace(/<\/?(p|div|h[1-6]|ul|ol|li)[^>]*>/gi, (m) =>
    m.startsWith('</') ? '' : '\n'
  );

  // Normalize table-structure tags to bare, lowercase, attribute-free tags
  // (drops any style=/class= the model may have added on them).
  s = s.replace(new RegExp(`<(${TABLE_TAG_ALTERNATION})\\b[^>]*>`, 'gi'), (m, tag) => `<${tag.toLowerCase()}>`);
  s = s.replace(new RegExp(`</(${TABLE_TAG_ALTERNATION})\\s*>`, 'gi'), (m, tag) => `</${tag.toLowerCase()}>`);

  s = s.replace(/<span([^>]*)>/gi, (match, attrs) => {
    const classMatch = /class\s*=\s*["']([^"']+)["']/i.exec(attrs);
    const cls = classMatch?.[1]?.trim();
    if (cls && ALLOWED_CLASS.has(cls)) {
      return `<span class="${cls}">`;
    }
    return '';
  });

  s = s.replace(/<br\s*\/?>/gi, '<br>');
  const nonWhitelistedTagRe = new RegExp(
    `<(?!/?span\\b|br\\s*/?>|/?(?:${TABLE_TAG_ALTERNATION})\\b)[^>]+>`,
    'gi'
  );
  s = s.replace(nonWhitelistedTagRe, '');
  s = s.replace(/<\/span>/gi, '</span>');
  s = s.replace(/\n{3,}/g, '\n\n');
  s = mergeAdjacentCptSpans(s);
  return s.trim();
};

const escapeCptInnerTextEarly = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** Wrap [bracket] placeholders as cpt-variable, the rest of the cell as cpt-common. */
const cellTextToCptSpans = (text) => {
  const raw = String(text || '');
  if (!raw) return `<span class="${CPT_HTML_CLASS.common}"></span>`;
  const parts = raw.split(/(\[[^\]]*\])/g).filter((p) => p !== '');
  return parts
    .map((p) =>
      /^\[[^\]]*\]$/.test(p)
        ? `<span class="${CPT_HTML_CLASS.variable}">${escapeCptInnerTextEarly(p)}</span>`
        : `<span class="${CPT_HTML_CLASS.common}">${escapeCptInnerTextEarly(p)}</span>`
    )
    .join('');
};

const wrapCommonProse = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  return `<span class="${CPT_HTML_CLASS.common}">${escapeCptInnerTextEarly(trimmed).replace(/\n/g, '<br>')}</span>`;
};

const buildCptTable = (headerCells, dataRows) => {
  const theadRow = `<tr>${headerCells.map((h) => `<th>${cellTextToCptSpans(h)}</th>`).join('')}</tr>`;
  const bodyRows = dataRows
    .map((row) => {
      const padded = [...row];
      while (padded.length < headerCells.length) padded.push('');
      return `<tr>${padded
        .slice(0, headerCells.length)
        .map((cell) => `<td>${cellTextToCptSpans(cell)}</td>`)
        .join('')}</tr>`;
    })
    .join('');
  return `<table><thead>${theadRow}</thead><tbody>${bodyRows}</tbody></table>`;
};

/**
 * Detect a markdown pipe table anywhere in plain LLM output and convert it —
 * plus any surrounding prose — into CPT-coloured HTML with a real <table>.
 * Models often reproduce a tabular base template this way even when told to
 * emit HTML directly.
 */
const convertMarkdownTableToCptHtml = (plainText) => {
  const lines = String(plainText || '').split(/\r?\n/);
  for (let i = 0; i < lines.length; i += 1) {
    const parsed = tryParseMarkdownPipeTable(lines, i);
    if (!parsed) continue;
    const { headerCells, dataRows, endExclusive } = parsed;
    const before = wrapCommonProse(lines.slice(0, i).join('\n'));
    const after = wrapCommonProse(lines.slice(endExclusive).join('\n'));
    return [before, buildCptTable(headerCells, dataRows), after].filter(Boolean).join('');
  }
  return '';
};

/**
 * Detect 2+ consecutive "Label: value" blocks (blank-line separated) that
 * repeat the same ordered label set — the shape models fall back to for
 * tabular content (e.g. an AE/SAE reporting schedule) when they skip table
 * markup entirely — and convert them into a real CPT-coloured HTML table.
 */
const LABELED_LINE_RE = /^([A-Za-z][A-Za-z0-9 /()'-]{1,60}):\s+(.+)$/;

const convertLabeledBlocksToCptHtml = (plainText) => {
  const raw = String(plainText || '').replace(/\r\n/g, '\n');
  const blocks = raw.split(/\n{2,}/);

  const parsedBlocks = blocks.map((block) => {
    const lines = block
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return null;
    const pairs = [];
    for (const line of lines) {
      const m = LABELED_LINE_RE.exec(line);
      if (!m) return null;
      pairs.push([m[1].trim(), m[2].trim()]);
    }
    return pairs;
  });

  const sameLabelSet = (a, b) =>
    a.length === b.length && a.every(([k], idx) => k.toLowerCase() === b[idx][0].toLowerCase());

  let bestStart = -1;
  let bestLen = 0;
  let i = 0;
  while (i < parsedBlocks.length) {
    if (!parsedBlocks[i] || parsedBlocks[i].length < 2) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < parsedBlocks.length && parsedBlocks[j] && sameLabelSet(parsedBlocks[i], parsedBlocks[j])) {
      j += 1;
    }
    const runLen = j - i;
    if (runLen > bestLen && runLen >= 2) {
      bestLen = runLen;
      bestStart = i;
    }
    i = j > i ? j : i + 1;
  }

  if (bestStart === -1) return '';

  const headerCells = parsedBlocks[bestStart].map(([k]) => k);
  const dataRows = parsedBlocks
    .slice(bestStart, bestStart + bestLen)
    .map((pairs) => pairs.map(([, v]) => v));

  const before = wrapCommonProse(blocks.slice(0, bestStart).join('\n\n'));
  const after = wrapCommonProse(blocks.slice(bestStart + bestLen).join('\n\n'));

  return [before, buildCptTable(headerCells, dataRows), after].filter(Boolean).join('');
};

const convertTabularTextToCptHtml = (plainText) =>
  convertMarkdownTableToCptHtml(plainText) || convertLabeledBlocksToCptHtml(plainText);

/** Prefer CPT HTML; if model/ADK returns plain text, wrap as common so Section 9 does not go blank. */
export const coerceCptTemplateHtml = (value) => {
  const sanitized = sanitizeCptTemplateHtml(value);
  if (sanitized) return sanitized;
  const plain = String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
  if (!plain) return '';

  const tableHtml = convertTabularTextToCptHtml(plain);
  if (tableHtml) return tableHtml;

  const escaped = plain
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
  return `<span class="${CPT_HTML_CLASS.common}">${escaped}</span>`;
};

const CPT_CLASS_PATTERN = 'cpt-(?:common|suggested|variable|example)';

/** Character-level tokens with CPT class for diff-based plain-text edits. */
export const tokenizeCptHtml = (html) => {
  const sanitized = sanitizeCptTemplateHtml(html);
  if (!sanitized) return [];

  const tokens = [];
  const spanRe = new RegExp(
    `<span class="(${CPT_CLASS_PATTERN})">([\\s\\S]*?)<\\/span>`,
    'gi'
  );
  let lastEnd = 0;
  let match;

  const pushChars = (text, cls) => {
    const normalized = String(text).replace(/<br\s*\/?>/gi, '\n');
    for (const ch of normalized) {
      tokens.push({ ch, cls });
    }
  };

  while ((match = spanRe.exec(sanitized)) !== null) {
    if (match.index > lastEnd) {
      pushChars(sanitized.slice(lastEnd, match.index), CPT_HTML_CLASS.common);
    }
    pushChars(match[2], match[1]);
    lastEnd = match.index + match[0].length;
  }
  if (lastEnd < sanitized.length) {
    pushChars(sanitized.slice(lastEnd), CPT_HTML_CLASS.common);
  }
  return tokens;
};

export const tokensToPlainText = (tokens) => tokens.map((t) => t.ch).join('');

const escapeCptInnerText = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const tokensToCptHtml = (tokens) => {
  if (!tokens.length) return '';

  const chunks = [];
  let cls = tokens[0].cls;
  let buf = '';

  const flush = () => {
    if (!buf) return;
    chunks.push(
      `<span class="${cls}">${escapeCptInnerText(buf).replace(/\n/g, '<br>')}</span>`
    );
    buf = '';
  };

  for (const token of tokens) {
    if (token.cls !== cls) {
      flush();
      cls = token.cls;
    }
    buf += token.ch;
  }
  flush();
  return mergeAdjacentCptSpans(chunks.join(''));
};

const LCS_CHAR_LIMIT = 2500;

const buildLcsDiffOps = (a, b) => {
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));

  for (let i = 1; i <= n; i += 1) {
    for (let j = 1; j <= m; j += 1) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const ops = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      ops.push({ type: 'keep', oi: i - 1 });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.push({ type: 'insert', ch: b[j - 1] });
      j -= 1;
    } else {
      ops.push({ type: 'delete' });
      i -= 1;
    }
  }
  ops.reverse();
  return ops;
};

const applyBracketVariableColouring = (tokens) => {
  const out = tokens.map((t) => ({ ...t }));
  let i = 0;
  while (i < out.length) {
    if (out[i].ch === '[') {
      let j = i + 1;
      while (j < out.length && out[j].ch !== ']') j += 1;
      if (j < out.length) {
        for (let k = i; k <= j; k += 1) {
          out[k].cls = CPT_HTML_CLASS.variable;
        }
        i = j + 1;
        continue;
      }
    }
    i += 1;
  }
  return out;
};

const getBracketRanges = (plain) => {
  const ranges = [];
  let i = 0;
  while (i < plain.length) {
    if (plain[i] === '[') {
      let j = i + 1;
      while (j < plain.length && plain[j] !== ']') j += 1;
      if (j < plain.length) {
        ranges.push([i, j]);
        i = j + 1;
        continue;
      }
    }
    i += 1;
  }
  return ranges;
};

const isInBracketRange = (idx, ranges) =>
  ranges.some(([start, end]) => idx >= start && idx <= end);

/** Indices in edited text that map to unchanged variable spans in the original HTML. */
const computeVariableKeptIndices = (origTokens, edited) => {
  const originalPlain = tokensToPlainText(origTokens);
  const ops = buildLcsDiffOps(originalPlain, edited);
  const kept = new Set();
  let editIdx = 0;
  for (const op of ops) {
    if (op.type === 'keep') {
      if (origTokens[op.oi]?.cls === CPT_HTML_CLASS.variable) {
        kept.add(editIdx);
      }
      editIdx += 1;
    } else if (op.type === 'insert') {
      editIdx += 1;
    }
  }
  return kept;
};

/**
 * Blue/green only where CPT allows: unchanged variable spans, [placeholders], or example kept chars.
 * New or edited text defaults to common (black) — never inherit variable from a neighbour.
 */
const reconcileColoursAfterPlainEdit = (tokens, origTokens, edited) => {
  const variableKept = computeVariableKeptIndices(origTokens, edited);
  const bracketRanges = getBracketRanges(edited);
  const exampleKept = new Set();
  const originalPlain = tokensToPlainText(origTokens);
  const ops = buildLcsDiffOps(originalPlain, edited);
  let editIdx = 0;
  for (const op of ops) {
    if (op.type === 'keep') {
      if (origTokens[op.oi]?.cls === CPT_HTML_CLASS.example) {
        exampleKept.add(editIdx);
      }
      editIdx += 1;
    } else if (op.type === 'insert') {
      editIdx += 1;
    }
  }

  const reconciled = tokens.map((t, i) => {
    if (t.cls === CPT_HTML_CLASS.variable) {
      if (variableKept.has(i) || isInBracketRange(i, bracketRanges)) return t;
      return { ...t, cls: CPT_HTML_CLASS.common };
    }
    if (t.cls === CPT_HTML_CLASS.example && !exampleKept.has(i)) {
      return { ...t, cls: CPT_HTML_CLASS.common };
    }
    return t;
  });

  return applyBracketVariableColouring(reconciled);
};

const mergePlainTextEditTokens = (origTokens, edited) => {
  const originalPlain = tokensToPlainText(origTokens);
  if (originalPlain.length > LCS_CHAR_LIMIT || edited.length > LCS_CHAR_LIMIT) {
    let pre = 0;
    const maxPre = Math.min(originalPlain.length, edited.length);
    while (pre < maxPre && originalPlain[pre] === edited[pre]) pre += 1;

    let suf = 0;
    const maxSuf = Math.min(originalPlain.length - pre, edited.length - pre);
    while (
      suf < maxSuf &&
      originalPlain[originalPlain.length - 1 - suf] === edited[edited.length - 1 - suf]
    ) {
      suf += 1;
    }

    const prefix = origTokens.slice(0, pre);
    const suffix = origTokens.slice(origTokens.length - suf);
    const midPlain = edited.slice(pre, edited.length - suf);
    const mid = [...midPlain].map((ch) => ({ ch, cls: CPT_HTML_CLASS.common }));
    return [...prefix, ...mid, ...suffix];
  }

  const ops = buildLcsDiffOps(originalPlain, edited);
  const result = [];
  for (const op of ops) {
    if (op.type === 'keep') {
      result.push(origTokens[op.oi]);
    } else if (op.type === 'insert') {
      result.push({ ch: op.ch, cls: CPT_HTML_CLASS.common });
    }
  }
  return result;
};

/**
 * Apply a plain-text edit while preserving CPT span classes from the original HTML.
 * Unchanged text keeps its colours; new/changed text is common (black) unless [brackets].
 */
export const applyPlainTextEditToCptHtml = (originalHtml, editedPlain) => {
  const edited = String(editedPlain ?? '');
  const origTokens = tokenizeCptHtml(originalHtml);

  if (!origTokens.length) {
    return edited;
  }

  const originalPlain = tokensToPlainText(origTokens);
  if (originalPlain === edited) {
    return sanitizeCptTemplateHtml(originalHtml);
  }

  const mergedTokens = reconcileColoursAfterPlainEdit(
    mergePlainTextEditTokens(origTokens, edited),
    origTokens,
    edited
  );

  const html = tokensToCptHtml(mergedTokens);
  const sanitized = sanitizeCptTemplateHtml(html);
  if (sanitized) return sanitized;

  if (!edited.trim()) return '';
  return tokensToCptHtml(
    applyBracketVariableColouring(
      [...edited].map((ch) => ({ ch, cls: CPT_HTML_CLASS.common }))
    )
  );
};

/** Merge consecutive spans of the same CPT class (fixes word-by-word LLM colouring). */
export const mergeAdjacentCptSpans = (html) => {
  if (!html) return '';
  let s = html;
  const classes = Object.values(CPT_HTML_CLASS);

  // Content groups exclude span boundaries so a lazy match can't "reach across"
  // an intervening different-class span (e.g. common-[variable]-common) and
  // wrongly fuse it into one span under the wrong class.
  const NO_SPAN_TAG = '(?:(?!</?span\\b)[\\s\\S])*?';

  for (const cls of classes) {
    const mergePattern = new RegExp(
      `(<span class="${cls}">)(${NO_SPAN_TAG})</span>(\\s*(?:<br\\s*/?>)?\\s*)<span class="${cls}">(${NO_SPAN_TAG})</span>`,
      'gi'
    );
    let changed = true;
    while (changed) {
      const next = s.replace(mergePattern, (_, open, a, sep, b) => {
        const joiner = /<br/i.test(sep) ? '<br>' : ' ';
        return `${open}${a}${joiner}${b}</span>`;
      });
      changed = next !== s;
      s = next;
    }
  }
  return s;
};

export const CPT_COLORED_HTML_OUTPUT_RULES = `COLOURED HTML OUTPUT (Section 9 & 11 — mandatory for this field):
Output minimal HTML using ONLY these span classes, plus <br> for line breaks and the table tags below when the content is tabular. No other tags, no style= attributes, no font/color tags.

<span class="cpt-common">...</span> — harmonized common regulatory text (black in CPT Word template)
<span class="cpt-suggested">...</span> — suggested optional language you chose to include (black in CPT)
<span class="cpt-variable">...</span> — bracket placeholders [X] or intake-resolved variable values (blue in CPT)
<span class="cpt-example">...</span> — ONLY intake-substantiated sentences that originated from example text (green italic in CPT)

TABLES: If the reference base content for this field is a table (e.g. a markdown "| ... |" table, such as an AE/SAE/pregnancy reporting schedule), reproduce it as a real HTML table so it renders as a grid — do NOT flatten rows into "Label: value" lines. Allowed table tags, bare (no attributes): <table> <thead> <tbody> <tr> <th> <td>. Every cell's text must still be wrapped in the appropriate cpt- span, e.g.:
<table><thead><tr><th><span class="cpt-common">Event Type</span></th><th><span class="cpt-common">Reportable Period Start</span></th></tr></thead><tbody><tr><td><span class="cpt-common">AE</span></td><td><span class="cpt-variable">28 days after last dose</span></td></tr></tbody></table>
Keep the same column headers and row structure as the base table; tailor cell values from clinical intake the same way you would tailor any other variable text.

COLOURING GRANULARITY (match CPT Word — do not over-fragment or under-colour):
- Use the natural CPT unit for each coloured segment:
  • Only a variable word or short phrase (e.g. [X], study name) → wrap ONLY that word/phrase in cpt-variable (blue).
  • An entire sentence of one category → one span for that sentence.
  • An entire paragraph or bullet of one category → one span for the full paragraph/bullet.
- If a whole paragraph is variable text in CPT, the WHOLE paragraph must be blue — not just [brackets] inside it.
- If a common paragraph contains one [placeholder], only the placeholder is blue; the rest stays cpt-common (black).
- Never split one continuous CPT block into multiple spans of the same class.
- Open a new span only when the CPT category changes.

Never output instructional (red) text. Never output <Start of …> markers.`;

/**
 * Purely presentational cleanup for the •/o bullet markers — these are
 * literal characters the model writes as ordinary text (see
 * CPT_COLORED_HTML_OUTPUT_RULES above, which forbids the model from ever
 * outputting real <ul>/<li>, and sanitizeCptTemplateHtml actively strips
 * those tags if the model does anyway), relying on a bare tab character
 * before "o" for the visual sub-bullet effect that renders as a flat,
 * un-indented "o" rather than a proper nested marker. This only reformats
 * the rendered preview — it never touches the underlying sanitized HTML
 * string that tokenizeCptHtml/applyPlainTextEditToCptHtml round-trip
 * through when the user edits, so it can't affect saving or colour
 * preservation on edit.
 *
 * Deliberately text-substitution only — swapping marker/whitespace
 * characters for other marker/whitespace characters — never inserting,
 * removing, or reordering any HTML tag. An earlier version of this function
 * split on <br> and wrapped each line in its own <div>, assuming one <span>
 * per bullet line; that assumption is false in practice — the model commonly
 * wraps an entire multi-bullet paragraph in ONE continuous <span> spanning
 * several <br>s (confirmed against real generated output), so per-line
 * wrapping left spans unclosed/reopened incorrectly and produced broken
 * HTML. Text substitution can't have that failure mode: the result has
 * exactly the same tag structure as the input, always.
 */
export function formatCptBulletsForDisplay(html) {
  if (!html) return html;
  const afterLineStart = '(^|<br\\s*/?>|<span class="cpt-[a-z]+">)';
  return String(html)
    .replace(new RegExp(`${afterLineStart}\\s*•\\t*`, 'gi'), '$1• ')
    .replace(new RegExp(`${afterLineStart}\\t\\s*o\\t*`, 'gi'), '$1&nbsp;&nbsp;&nbsp;&nbsp;○ ');
}

export const cptTemplateHtmlSx = {
  fontSize: '14px',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  '& .cpt-common': { color: '#212121' },
  '& .cpt-suggested': { color: '#212121' },
  '& .cpt-variable': { color: '#1565C0' },
  '& .cpt-example': { color: '#2E7D32', fontStyle: 'italic' },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '8px 0',
    whiteSpace: 'normal',
  },
  '& th, & td': {
    border: '1px solid #ccc',
    padding: '6px 8px',
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& th': {
    backgroundColor: '#f0f0f0',
    fontWeight: 700,
  },
};
